/* eslint-disable no-unused-vars */
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import "./Affiliate.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useEffect, useState } from "react";
import {
  GetService,
  PostFormData,
  PostService,
  PutFormData,
} from "../../../Services/ConstantService";
import { API_URL } from "../../../Services/ApiService";
import { toastEmmit } from "../../../Helper/Toastr";
import { APIBaseURL } from "../../../Environment/Environment";

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  phone: "",
  dateOfBirth: "",
  workingProfession: "",

  affiliateType: "",
  businessName: "",
  einNumber: "",
  socialSecurityNumber: "",
  website: "",

  streetAddress: "",
  city: "",
  state: "",
  zipCode: "",
  country: "",

  promotionMethods: [],

  socialLinks: {
    instagram: "",
    facebook: "",
    youtube: "",
    tiktok: "",
    pinterest: "",
    linkedin: "",
  },

  audienceSize: "",
  reason: "",

  paymentMethod: "",

  paypalEmail: "",

  accountHolderName: "",
  bankName: "",
  accountNumber: "",
  routingNumber: "",
  swiftCode: "",
  iban: "",

  termsAccepted: false,
  informationConfirmed: false,
};

const getProfileImageUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return new URL(
    String(path).replace(/^\/+/, ""),
    `${APIBaseURL.replace(/\/$/, "")}/`,
  ).toString();
};

const parseProfileValue = (value, fallback) => {
  if (Array.isArray(value) || (value && typeof value === "object")) {
    return value;
  }

  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error("Unable to parse affiliate profile data:", error);
      return fallback;
    }
  }

  return fallback;
};

const validationSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters"),

  lastName: Yup.string()
    .trim()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters"),

  email: Yup.string()
    .trim()
    .email("Please enter a valid email address")
    .required("Email address is required"),

  password: Yup.string()
    .trim()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters"),

  phone: Yup.string()
    .required("Mobile number is required")
    .test("valid-phone", "Please enter a valid mobile number", (value) => {
      if (!value) return false;

      const digits = value.replace(/\D/g, "");

      return digits.length >= 8 && digits.length <= 15;
    }),

  dateOfBirth: Yup.string().nullable(),

  workingProfession: Yup.string().required("Please select your profession"),

  affiliateType: Yup.string().required("Please select influencer type"),

  businessName: Yup.string().when("affiliateType", {
    is: "Business",
    then: (schema) => schema.trim().required("Business name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  einNumber: Yup.string().when("affiliateType", {
    is: "Business",
    then: (schema) => schema.trim().required("EIN number is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  socialSecurityNumber: Yup.string().when("affiliateType", {
    is: "Individual",
    then: (schema) =>
      schema.trim().required("Social Security Number is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  website: Yup.string()
    .trim()
    .url("Please enter a valid website URL")
    .nullable(),

  streetAddress: Yup.string().trim().required("Street address is required"),

  city: Yup.string().trim().required("City is required"),

  state: Yup.string().trim().required("State / Province is required"),

  zipCode: Yup.string().trim().required("ZIP / Postal Code is required"),

  country: Yup.string().trim().required("Country is required"),

  promotionMethods: Yup.array()
    .of(Yup.string())
    .min(1, "Please select at least one promotion method"),

  audienceSize: Yup.string().nullable(),

  reason: Yup.string()
    .trim()
    .required("Please tell us why you want to join")
    .min(20, "Please provide at least 20 characters"),

  paymentMethod: Yup.string().required("Please select a payment method"),

  paypalEmail: Yup.string().when("paymentMethod", {
    is: "paypal",
    then: (schema) =>
      schema
        .email("Please enter a valid PayPal email")
        .required("PayPal email is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  accountHolderName: Yup.string().when("paymentMethod", {
    is: "bank_transfer",
    then: (schema) => schema.required("Account holder name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  bankName: Yup.string().when("paymentMethod", {
    is: "bank_transfer",
    then: (schema) => schema.required("Bank name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  accountNumber: Yup.string().when("paymentMethod", {
    is: "bank_transfer",
    then: (schema) =>
      schema
        .required("Account number is required")
        .min(4, "Please enter a valid account number"),
    otherwise: (schema) => schema.notRequired(),
  }),

  routingNumber: Yup.string().when("paymentMethod", {
    is: "bank_transfer",
    then: (schema) =>
      schema
        .required("Routing number is required")
        .matches(/^[0-9]{9}$/, "Routing number must contain 9 digits"),
    otherwise: (schema) => schema.notRequired(),
  }),

  swiftCode: Yup.string().nullable(),

  iban: Yup.string().nullable(),

  termsAccepted: Yup.boolean().oneOf(
    [true],
    "You must accept the Influencer Program Terms & Conditions",
  ),

  informationConfirmed: Yup.boolean().oneOf(
    [true],
    "Please confirm that the information is accurate",
  ),
});

const editValidationSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters"),
  lastName: Yup.string()
    .trim()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters"),
  dateOfBirth: Yup.string().nullable(),
  workingProfession: Yup.string().required("Please select your profession"),
  website: Yup.string()
    .trim()
    .url("Please enter a valid website URL")
    .nullable(),
  streetAddress: Yup.string().trim().required("Street address is required"),
  city: Yup.string().trim().required("City is required"),
  state: Yup.string().trim().required("State / Province is required"),
  zipCode: Yup.string().trim().required("ZIP / Postal Code is required"),
  country: Yup.string().trim().required("Country is required"),
  promotionMethods: Yup.array()
    .of(Yup.string())
    .min(1, "Please select at least one promotion method"),
});

const AffiliateSignup = ({ mode }) => {
  const isEditMode = mode === "edit";
  const [CountryList, setCountryList] = useState([]);
  const [StateList, setStateList] = useState([]);
  const [loading, setloading] = useState(false);
  const [profileInitialValues, setProfileInitialValues] =
    useState(initialValues);
  const [profileData, setProfileData] = useState(null);
  const [countryListLoaded, setCountryListLoaded] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [profileLoading, setProfileLoading] = useState(isEditMode);
  const [profileLoadError, setProfileLoadError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    GetCountryList();
  }, []);

  useEffect(() => {
    if (!isEditMode) return undefined;

    let isMounted = true;

    GetService(API_URL.GET_AFFILIATE_PROFILE)
      .then((res) => {
        if (!res?.data?.status || !res?.data?.data) {
          throw new Error(res?.data?.message || "Unable to load your profile.");
        }

        if (isMounted) {
          setProfileData(res.data.data);
          setProfileImageUrl(
            getProfileImageUrl(res.data.data.image_url || res.data.data.image),
          );
        }
      })
      .catch((error) => {
        if (isMounted) {
          toastEmmit(
            "error",
            error?.response?.data?.message ||
              error?.message ||
              "Unable to load your profile.",
          );
          setProfileLoadError(true);
          setProfileLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isEditMode]);

  useEffect(() => {
    if (!selectedImage) {
      setImagePreview("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(selectedImage);
    setImagePreview(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [selectedImage]);

  const GetCountryList = () => {
    setloading(true);

    GetService(API_URL.GET_COUNTRY_LIST)
      .then((res) => {
        if (res?.data?.status) {
          setCountryList(res?.data?.data || []);
        } else {
          setCountryList([]);
        }
      })
      .catch((error) => {
        toastEmmit("error", error?.response?.data?.message);
        setCountryList([]);
      })
      .finally(() => {
        setloading(false);
        setCountryListLoaded(true);
      });
  };

  useEffect(() => {
    if (!isEditMode || !profileData || !countryListLoaded) {
      return;
    }

    let isMounted = true;
    const profileCountry = profileData.country;
    const country = CountryList.find(
      (item) =>
        String(item.country_id) === String(profileCountry) ||
        item.country_name?.toLowerCase() ===
          String(profileCountry || "").toLowerCase(),
    );
    const countryId = country?.country_id || "";

    const setProfileValues = (states = []) => {
      if (!isMounted) return;
      setStateList(states);

      const profileState = profileData.state;
      const state = states.find(
        (item) =>
          String(item.state_id) === String(profileState) ||
          item.state_name?.toLowerCase() ===
            String(profileState || "").toLowerCase(),
      );
      const socialLinks = parseProfileValue(profileData.socialLinks, {});
      const promotionMethods = parseProfileValue(
        profileData.promotionMethods,
        [],
      );
      const profileDate = profileData.dateOfBirth || "";

      setProfileInitialValues({
        ...initialValues,
        ...profileData,
        country: countryId || profileCountry || "",
        state: state?.state_id || "",
        dateOfBirth:
          typeof profileDate === "string" ? profileDate.slice(0, 10) : "",
        socialLinks:
          socialLinks && typeof socialLinks === "object"
            ? { ...initialValues.socialLinks, ...socialLinks }
            : initialValues.socialLinks,
        promotionMethods: Array.isArray(promotionMethods)
          ? promotionMethods
          : [],
      });
      setProfileLoading(false);
    };

    if (!countryId) {
      setProfileValues();
      return () => {
        isMounted = false;
      };
    }

    GetService(`${API_URL.GET_STATE_LIST}?country_id=${countryId}`)
      .then((res) => {
        if (res?.data?.status && Array.isArray(res?.data?.data)) {
          setProfileValues(res.data.data);
          return;
        }

        if (isMounted) {
          toastEmmit("error", res?.data?.message || "Unable to load states.");
        }
        setProfileValues();
      })
      .catch((error) => {
        if (isMounted) {
          toastEmmit(
            "error",
            error?.response?.data?.message || "Unable to load states.",
          );
        }
        setProfileValues();
      });

    return () => {
      isMounted = false;
    };
  }, [CountryList, countryListLoaded, isEditMode, profileData]);

  const GetStateList = (cid) => {
    setloading(true);

    GetService(`${API_URL.GET_STATE_LIST}?country_id=${cid}`)
      .then((res) => {
        if (res?.data?.status) {
          setStateList(res?.data?.data || []);
        } else {
          setStateList([]);
        }
      })
      .catch((error) => {
        toastEmmit("error", error?.response?.data?.message);
        setStateList([]);
      })
      .finally(() => {
        setloading(false);
      });
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const selectedCountry = CountryList.find(
      (c) => c.country_id === Number(values.country),
    );

    const selectedState = StateList.find(
      (c) => c.state_id === Number(values.state),
    );
    const payload = {
      ...values,
      country: selectedCountry?.country_name || "",
      state: selectedState?.state_name || "",
    };

    try {
      let res;
      if (isEditMode) {
        const editableProfile = {
          firstName: values.firstName,
          lastName: values.lastName,
          dateOfBirth: values.dateOfBirth,
          workingProfession: values.workingProfession,
          website: values.website,
          streetAddress: values.streetAddress,
          city: values.city,
          state: payload.state,
          zipCode: values.zipCode,
          country: payload.country,
          promotionMethods: values.promotionMethods,
          socialLinks: values.socialLinks,
          audienceSize: values.audienceSize,
        };
        const formData = new FormData();
        Object.entries(editableProfile).forEach(([key, value]) => {
          formData.append(
            key,
            value && typeof value === "object"
              ? JSON.stringify(value)
              : value || "",
          );
        });
        if (selectedImage) formData.append("image", selectedImage);
        res = await PutFormData(API_URL.UPDATE_AFFILIATE_PROFILE, formData);
      } else if (selectedImage) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          formData.append(
            key,
            value && typeof value === "object"
              ? JSON.stringify(value)
              : value || "",
          );
        });
        formData.append("image", selectedImage);
        res = await PostFormData(API_URL.AFFILIATE_SIGNUP, formData);
      } else {
        res = await PostService(API_URL.AFFILIATE_SIGNUP, payload);
      }

      if (res?.data?.status) {
        toastEmmit(
          "success",
          isEditMode
            ? "Your profile has been updated."
            : "Your request has been submitted. We will get back to you soon.",
        );
        resetForm();
        setSelectedImage(null);
        navigate(isEditMode ? "/profile-dashboard/affiliate-dash" : "/home");
      } else if (isEditMode || selectedImage) {
        toastEmmit(
          "error",
          res?.data?.message || "Unable to save your profile.",
        );
      }
    } catch (error) {
      toastEmmit(
        "error",
        error?.response?.data?.message || "Something went wrong",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="pt-20 pb-100 affiliate-signup-page">
      <div className="container">
        {/* Header */}
        <div className="row justify-content-center mb-5">
          <div className="col-12">
            <Link
              to={
                isEditMode
                  ? "/profile-dashboard/affiliate-dash"
                  : "/affiliateProgram"
              }
              className="text-decoration-none d-inline-flex align-items-center mb-4"
            >
              <ArrowLeft size={18} className="me-2" />
              Back
            </Link>

            <div className="text-center row text">
              <h2 className="text-left second">
                {isEditMode
                  ? "Edit Influencer Profile"
                  : "Join Our Influencer Program"}
              </h2>

              {!isEditMode && (
                <p className="text-muted">
                  Complete the form below to apply for the Printlific Influencer
                  Program. Your application will be reviewed by our team before
                  your account is approved.
                </p>
              )}
            </div>
          </div>
        </div>

        {isEditMode && profileLoading ? (
          <p className="text-center">
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            />{" "}
            Loading profile...{" "}
          </p>
        ) : isEditMode && profileLoadError ? (
          <p className="text-center text-danger">
            Unable to load your profile. Please return to the dashboard and try
            again.
          </p>
        ) : (
          <Formik
            initialValues={isEditMode ? profileInitialValues : initialValues}
            enableReinitialize={isEditMode}
            validationSchema={
              isEditMode ? editValidationSchema : validationSchema
            }
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
            }) => (
              <Form noValidate>
                <div className="row justify-content-center">
                  <div className="col-12">
                    {/* Personal Information */}
                    <div className="affiliate-form-card mb-4">
                      <div className="affiliate-form-header">
                        <h4>1. Personal Information</h4>
                        <p>Tell us a little about yourself.</p>
                      </div>

                      <div className="row g-4">
                        <div className="col-md-6">
                          <label className="form-label">
                            First Name <span className="text-danger">*</span>
                          </label>

                          <Field
                            name="firstName"
                            type="text"
                            className={`form-control ${
                              touched.firstName && errors.firstName
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Enter first name"
                          />

                          <ErrorMessage
                            name="firstName"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            Last Name <span className="text-danger">*</span>
                          </label>

                          <Field
                            name="lastName"
                            type="text"
                            className={`form-control ${
                              touched.lastName && errors.lastName
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Enter last name"
                          />

                          <ErrorMessage
                            name="lastName"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>

                        {!isEditMode && (
                          <>
                            <div className="col-md-6">
                              <label className="form-label">
                                Email Address{" "}
                                <span className="text-danger">*</span>
                              </label>

                              <Field
                                name="email"
                                type="email"
                                className={`form-control ${
                                  touched.email && errors.email
                                    ? "is-invalid"
                                    : ""
                                }`}
                                placeholder="Enter email address"
                              />

                              <ErrorMessage
                                name="email"
                                component="div"
                                className="invalid-feedback"
                              />
                            </div>

                            {(
                              // !localStorage.getItem("token") ||
                              // localStorage.getItem("userType") ===
                              //   "guestLogin") &&
                              //    (
                              <div className="col-md-6">
                                <label className="form-label">
                                  Password{" "}
                                  <span className="text-danger">*</span>
                                </label>

                                <div className="input-group">
                                  <Field
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    className={`form-control ${
                                      touched.password && errors.password
                                        ? "is-invalid"
                                        : ""
                                    }`}
                                    placeholder="Enter password here.."
                                  />

                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() =>
                                      setShowPassword((prev) => !prev)
                                    }
                                    aria-label={
                                      showPassword
                                        ? "Hide password"
                                        : "Show password"
                                    }
                                  >
                                    <i
                                      className={
                                        showPassword
                                          ? "fa-regular fa-eye-slash"
                                          : "fa-regular fa-eye"
                                      }
                                    />
                                  </button>
                                </div>

                                <ErrorMessage
                                  name="password"
                                  component="div"
                                  className="invalid-feedback d-block"
                                />
                              </div>
                            )}

                            <div className="col-md-6">
                              <label className="form-label">
                                Mobile Number{" "}
                                <span className="text-danger">*</span>
                              </label>

                              <PhoneInput
                                country="us"
                                value={values.phone}
                                onChange={(phone) => {
                                  setFieldValue(
                                    "phone",
                                    phone ? `+${phone}` : "",
                                  );
                                }}
                                onBlur={() => {
                                  setFieldTouched("phone", true);
                                }}
                                inputProps={{
                                  name: "phone",
                                  required: true,
                                  autoComplete: "tel",
                                }}
                                enableSearch
                                countryCodeEditable={false}
                                containerClass={
                                  touched.phone && errors.phone
                                    ? "phone-input-error"
                                    : ""
                                }
                                inputClass="form-control"
                              />

                              {touched.phone && errors.phone && (
                                <div className="text-danger small mt-1">
                                  {errors.phone}
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        <div className="col-md-6">
                          <label className="form-label">
                            Working Profession{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <Field
                            as="select"
                            name="workingProfession"
                            disabled={isSubmitting}
                            className={`form-select ${
                              touched.workingProfession &&
                              errors.workingProfession
                                ? "is-invalid"
                                : ""
                            }`}
                          >
                            <option value="" hidden>
                              Select your profession
                            </option>
                            <option value="Influencer">Influencer</option>
                            <option value="Photographer">Photographer</option>
                            <option value="Content Creator">
                              Content Creator
                            </option>
                            <option value="Blogger">Blogger</option>
                            <option value="Social Media Manager">
                              Social Media Manager
                            </option>
                            <option value="Marketing Professional">
                              Marketing Professional
                            </option>
                            <option value="Agency / Marketing Agency">
                              Agency / Marketing Agency
                            </option>
                            <option value="Other">Other</option>
                          </Field>
                          <ErrorMessage
                            name="workingProfession"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            Profile Image{" "}
                            <span className="text-muted">(Optional)</span>
                          </label>
                          <input
                            type="file"
                            name="image"
                            accept="image/*"
                            className="form-control"
                            onChange={(event) =>
                              setSelectedImage(
                                event.currentTarget.files?.[0] || null,
                              )
                            }
                          />
                          {(imagePreview || profileImageUrl) && (
                            <img
                              src={imagePreview || profileImageUrl}
                              alt="Profile preview"
                              className="rounded-circle mt-3"
                              width={80}
                              height={80}
                              style={{ objectFit: "cover" }}
                            />
                          )}
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            Date of Birth{" "}
                            <span className="text-muted">(Optional)</span>
                          </label>

                          <Field
                            name="dateOfBirth"
                            type="date"
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Business Information */}
                    <div className="affiliate-form-card mb-4">
                      <div className="affiliate-form-header">
                        <h4>
                          {isEditMode
                            ? "Website & Location"
                            : "2. Business Information"}
                        </h4>
                        <p>
                          {isEditMode
                            ? "Update your website and location details."
                            : "Tell us about your business or professional activity."}
                        </p>
                      </div>

                      <div className="row g-4">
                        {!isEditMode && (
                          <div className="col-md-6">
                            <label className="form-label">
                              Influencer Type{" "}
                              <span className="text-danger">*</span>
                            </label>

                            <Field
                              as="select"
                              name="affiliateType"
                              onChange={(event) => {
                                const affiliateType = event.target.value;
                                setFieldValue("affiliateType", affiliateType);
                                setFieldValue(
                                  "businessName",
                                  affiliateType === "Business"
                                    ? values.businessName
                                    : "",
                                );
                                setFieldValue(
                                  "einNumber",
                                  affiliateType === "Business"
                                    ? values.einNumber
                                    : "",
                                );
                                setFieldValue(
                                  "socialSecurityNumber",
                                  affiliateType === "Individual"
                                    ? values.socialSecurityNumber
                                    : "",
                                );
                              }}
                              className={`form-select ${
                                touched.affiliateType && errors.affiliateType
                                  ? "is-invalid"
                                  : ""
                              }`}
                            >
                              <option value="" hidden>
                                Select influencer type
                              </option>
                              <option value="Individual">Individual</option>
                              <option value="Business">Business</option>
                            </Field>

                            <ErrorMessage
                              name="affiliateType"
                              component="div"
                              className="invalid-feedback"
                            />
                          </div>
                        )}

                        {!isEditMode && values.affiliateType === "Business" && (
                          <div className="col-md-6">
                            <label className="form-label">
                              Business Name{" "}
                              <span className="text-danger">*</span>
                            </label>

                            <Field
                              name="businessName"
                              type="text"
                              className={`form-control ${
                                touched.businessName && errors.businessName
                                  ? "is-invalid"
                                  : ""
                              }`}
                              placeholder="Enter business name"
                            />

                            <ErrorMessage
                              name="businessName"
                              component="div"
                              className="invalid-feedback"
                            />
                          </div>
                        )}

                        {!isEditMode && values.affiliateType === "Business" && (
                          <div className="col-md-6">
                            <label className="form-label">
                              EIN Number <span className="text-danger">*</span>
                            </label>
                            <Field
                              name="einNumber"
                              type="text"
                              className={`form-control ${
                                touched.einNumber && errors.einNumber
                                  ? "is-invalid"
                                  : ""
                              }`}
                              placeholder="Enter EIN number"
                            />
                            <ErrorMessage
                              name="einNumber"
                              component="div"
                              className="invalid-feedback"
                            />
                          </div>
                        )}

                        {!isEditMode &&
                          values.affiliateType === "Individual" && (
                            <div className="col-md-6">
                              <label className="form-label">
                                Social Security Number{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <Field
                                name="socialSecurityNumber"
                                type="text"
                                className={`form-control ${
                                  touched.socialSecurityNumber &&
                                  errors.socialSecurityNumber
                                    ? "is-invalid"
                                    : ""
                                }`}
                                placeholder="Enter Social Security Number"
                              />
                              <ErrorMessage
                                name="socialSecurityNumber"
                                component="div"
                                className="invalid-feedback"
                              />
                            </div>
                          )}

                        <div className="col-12">
                          <label className="form-label">
                            Website{" "}
                            <span className="text-muted">(Optional)</span>
                          </label>

                          <Field
                            name="website"
                            type="url"
                            className={`form-control ${
                              touched.website && errors.website
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="https://example.com"
                          />

                          <ErrorMessage
                            name="website"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="col-12">
                          <label className="form-label">
                            Street Address{" "}
                            <span className="text-danger">*</span>
                          </label>

                          <Field
                            name="streetAddress"
                            type="text"
                            className={`form-control ${
                              touched.streetAddress && errors.streetAddress
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Enter street address"
                          />

                          <ErrorMessage
                            name="streetAddress"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            Country <span className="text-danger">*</span>
                          </label>

                          <Field
                            as="select"
                            name="country"
                            className={`form-select ${
                              touched.country && errors.country
                                ? "is-invalid"
                                : ""
                            }`}
                            onChange={(e) => {
                              const countryId = e.target.value;

                              // Update Formik country
                              setFieldValue("country", countryId);

                              // Clear previously selected state
                              setFieldValue("state", "");

                              // Clear previous state list
                              setStateList([]);

                              // Get states for selected country
                              if (countryId) {
                                GetStateList(countryId);
                              }
                            }}
                          >
                            <option value="" hidden>
                              Select Country
                            </option>

                            {CountryList.map((country) => (
                              <option
                                key={country.country_id}
                                value={country.country_id}
                              >
                                {country.country_name}
                              </option>
                            ))}
                          </Field>

                          <ErrorMessage
                            name="country"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            State / Province{" "}
                            <span className="text-danger">*</span>
                          </label>

                          <Field
                            as="select"
                            name="state"
                            disabled={!values.country || StateList.length === 0}
                            className={`form-select ${
                              touched.state && errors.state ? "is-invalid" : ""
                            }`}
                          >
                            <option value="" hidden>
                              {!values.country
                                ? "Select Country First"
                                : StateList.length === 0
                                  ? "No States Found"
                                  : "Select State / Province"}
                            </option>

                            {StateList.map((state) => (
                              <option
                                key={state.state_id}
                                value={state.state_id}
                              >
                                {state.state_name}
                              </option>
                            ))}
                          </Field>

                          <ErrorMessage
                            name="state"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            City <span className="text-danger">*</span>
                          </label>

                          <Field
                            name="city"
                            type="text"
                            className={`form-control ${
                              touched.city && errors.city ? "is-invalid" : ""
                            }`}
                            placeholder="Enter city"
                          />

                          <ErrorMessage
                            name="city"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            ZIP / Postal Code{" "}
                            <span className="text-danger">*</span>
                          </label>

                          <Field
                            name="zipCode"
                            type="text"
                            className={`form-control ${
                              touched.zipCode && errors.zipCode
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Enter ZIP / Postal code"
                          />

                          <ErrorMessage
                            name="zipCode"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Promotion */}
                    <div className="affiliate-form-card mb-4">
                      <div className="affiliate-form-header">
                        <h4>3. Promotion Information</h4>
                        <p>
                          Help us understand how you plan to promote printlific.
                        </p>
                      </div>

                      <div className="mb-4">
                        <label className="form-label d-block">
                          How do you plan to promote printlific?{" "}
                          <span className="text-danger">*</span>
                        </label>

                        <div className="row g-3">
                          {[
                            ["website", "Website"],
                            ["blog", "Blog"],
                            ["instagram", "Instagram"],
                            ["facebook", "Facebook"],
                            ["youtube", "YouTube"],
                            ["tiktok", "TikTok"],
                            ["pinterest", "Pinterest"],
                            ["email", "Email Marketing"],
                            ["word_of_mouth", "Word of Mouth"],
                            ["other", "Other"],
                          ].map(([value, label]) => (
                            <div className="col-md-4 col-sm-6" key={value}>
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  id={`promotion-${value}`}
                                  className="form-check-input"
                                  checked={values.promotionMethods.includes(
                                    value,
                                  )}
                                  onChange={(e) => {
                                    const current = values.promotionMethods;

                                    if (e.target.checked) {
                                      setFieldValue("promotionMethods", [
                                        ...current,
                                        value,
                                      ]);
                                    } else {
                                      setFieldValue(
                                        "promotionMethods",
                                        current.filter(
                                          (item) => item !== value,
                                        ),
                                      );
                                    }
                                  }}
                                />

                                <label
                                  htmlFor={`promotion-${value}`}
                                  className="form-check-label"
                                >
                                  {label}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>

                        {touched.promotionMethods &&
                          errors.promotionMethods && (
                            <div className="text-danger small mt-2">
                              {errors.promotionMethods}
                            </div>
                          )}
                      </div>

                      <div className="row g-4">
                        <div className="col-md-6">
                          <label className="form-label">
                            Instagram Profile
                          </label>

                          <Field
                            name="socialLinks.instagram"
                            type="url"
                            className="form-control"
                            placeholder="https://instagram.com/..."
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Facebook Profile</label>

                          <Field
                            name="socialLinks.facebook"
                            type="url"
                            className="form-control"
                            placeholder="https://facebook.com/..."
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">YouTube Channel</label>

                          <Field
                            name="socialLinks.youtube"
                            type="url"
                            className="form-control"
                            placeholder="https://youtube.com/..."
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">TikTok Profile</label>

                          <Field
                            name="socialLinks.tiktok"
                            type="url"
                            className="form-control"
                            placeholder="https://tiktok.com/@..."
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            Pinterest Profile
                          </label>

                          <Field
                            name="socialLinks.pinterest"
                            type="url"
                            className="form-control"
                            placeholder="https://pinterest.com/..."
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">LinkedIn Profile</label>

                          <Field
                            name="socialLinks.linkedin"
                            type="url"
                            className="form-control"
                            placeholder="https://linkedin.com/in/..."
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            Estimated Monthly Audience
                          </label>

                          <Field
                            as="select"
                            name="audienceSize"
                            className="form-select"
                          >
                            <option value="">Select audience size</option>
                            <option value="less_than_1000">
                              Less than 1,000
                            </option>
                            <option value="1000-10,000">1,000 – 10,000</option>
                            <option value="10,000-50,000">
                              10,000 – 50,000
                            </option>
                            <option value="50,000-100,000">
                              50,000 – 100,000
                            </option>
                            <option value="100,000+">100,000+</option>
                          </Field>
                        </div>

                        {!isEditMode && (
                          <div className="col-12">
                            <label className="form-label">
                              Why would you like to join the Printlific
                              Influencer Program?{" "}
                              <span className="text-danger">*</span>
                            </label>

                            <Field
                              as="textarea"
                              name="reason"
                              rows={3}
                              className={`form-control ${
                                touched.reason && errors.reason
                                  ? "is-invalid"
                                  : ""
                              }`}
                              placeholder="Tell us about yourself and how you plan to promote printlific..."
                            />

                            <ErrorMessage
                              name="reason"
                              component="div"
                              className="invalid-feedback"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {!isEditMode && (
                      <>
                        {/* Payment */}
                        <div className="affiliate-form-card mb-4">
                          <div className="affiliate-form-header">
                            <h4>4. Payment Information</h4>
                            <p>
                              Select how you would like to receive your
                              influencer commissions.
                            </p>
                          </div>

                          <div className="mb-4">
                            <label className="form-label d-block">
                              Preferred Payment Method{" "}
                              <span className="text-danger">*</span>
                            </label>

                            <div className="row">
                              <div className="col-md-6">
                                <div
                                  className="form-check payment-option"
                                  onClick={() =>
                                    setFieldValue("paymentMethod", "paypal")
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="paymentMethod"
                                    id="paypal"
                                    value="paypal"
                                    checked={values.paymentMethod === "paypal"}
                                    onChange={() =>
                                      setFieldValue("paymentMethod", "paypal")
                                    }
                                  />

                                  <label
                                    className="form-check-label"
                                    htmlFor="paypal"
                                  >
                                    PayPal
                                  </label>
                                </div>
                              </div>

                              <div className="col-md-6">
                                <div
                                  className="form-check payment-option"
                                  onClick={() =>
                                    setFieldValue(
                                      "paymentMethod",
                                      "bank_transfer",
                                    )
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="paymentMethod"
                                    id="bank_transfer"
                                    value="bank_transfer"
                                    checked={
                                      values.paymentMethod === "bank_transfer"
                                    }
                                    onChange={() =>
                                      setFieldValue(
                                        "paymentMethod",
                                        "bank_transfer",
                                      )
                                    }
                                  />

                                  <label
                                    className="form-check-label"
                                    htmlFor="bank_transfer"
                                  >
                                    Bank Transfer
                                  </label>
                                </div>
                              </div>
                            </div>

                            {touched.paymentMethod && errors.paymentMethod && (
                              <div className="text-danger small mt-2">
                                {errors.paymentMethod}
                              </div>
                            )}
                          </div>

                          {values.paymentMethod === "paypal" && (
                            <div className="row">
                              <div className="col-md-6">
                                <label className="form-label">
                                  PayPal Email{" "}
                                  <span className="text-danger">*</span>
                                </label>

                                <Field
                                  name="paypalEmail"
                                  type="email"
                                  className={`form-control ${
                                    touched.paypalEmail && errors.paypalEmail
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  placeholder="Enter PayPal email"
                                />

                                <ErrorMessage
                                  name="paypalEmail"
                                  component="div"
                                  className="invalid-feedback"
                                />
                              </div>
                            </div>
                          )}

                          {values.paymentMethod === "bank_transfer" && (
                            <div className="row g-4">
                              <div className="col-md-6">
                                <label className="form-label">
                                  Account Holder Name{" "}
                                  <span className="text-danger">*</span>
                                </label>

                                <Field
                                  name="accountHolderName"
                                  type="text"
                                  className={`form-control ${
                                    touched.accountHolderName &&
                                    errors.accountHolderName
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  placeholder="Enter account holder name"
                                />

                                <ErrorMessage
                                  name="accountHolderName"
                                  component="div"
                                  className="invalid-feedback"
                                />
                              </div>

                              <div className="col-md-6">
                                <label className="form-label">
                                  Bank Name{" "}
                                  <span className="text-danger">*</span>
                                </label>

                                <Field
                                  name="bankName"
                                  type="text"
                                  className={`form-control ${
                                    touched.bankName && errors.bankName
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  placeholder="Enter bank name"
                                />

                                <ErrorMessage
                                  name="bankName"
                                  component="div"
                                  className="invalid-feedback"
                                />
                              </div>

                              <div className="col-md-6">
                                <label className="form-label">
                                  Account Number{" "}
                                  <span className="text-danger">*</span>
                                </label>

                                <Field
                                  name="accountNumber"
                                  type="password"
                                  className={`form-control ${
                                    touched.accountNumber &&
                                    errors.accountNumber
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  placeholder="Enter account number"
                                />

                                <ErrorMessage
                                  name="accountNumber"
                                  component="div"
                                  className="invalid-feedback"
                                />
                              </div>

                              <div className="col-md-6">
                                <label className="form-label">
                                  Routing Number{" "}
                                  <span className="text-danger">*</span>
                                </label>

                                <Field
                                  name="routingNumber"
                                  type="text"
                                  inputMode="numeric"
                                  className={`form-control ${
                                    touched.routingNumber &&
                                    errors.routingNumber
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  placeholder="9 digit routing number"
                                />

                                <ErrorMessage
                                  name="routingNumber"
                                  component="div"
                                  className="invalid-feedback"
                                />
                              </div>

                              <div className="col-md-6">
                                <label className="form-label">
                                  SWIFT / BIC Code{" "}
                                  <span className="text-muted">(Optional)</span>
                                </label>

                                <Field
                                  name="swiftCode"
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter SWIFT / BIC"
                                />
                              </div>

                              <div className="col-md-6">
                                <label className="form-label">
                                  IBAN{" "}
                                  <span className="text-muted">(Optional)</span>
                                </label>

                                <Field
                                  name="iban"
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter IBAN"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Agreements */}
                    {!isEditMode && (
                      <div className="affiliate-form-card mb-4">
                        <div className="affiliate-form-header">
                          <h4>5. Agreement</h4>
                        </div>

                        <div className="form-check mb-3">
                          <Field
                            type="checkbox"
                            name="termsAccepted"
                            id="termsAccepted"
                            className={`form-check-input ${
                              touched.termsAccepted && errors.termsAccepted
                                ? "is-invalid"
                                : ""
                            }`}
                          />

                          <label
                            htmlFor="termsAccepted"
                            className="form-check-label"
                          >
                            I agree to the Printlific Influencer Program Terms &
                            Conditions.
                            <span className="text-danger"> *</span>
                          </label>

                          <ErrorMessage
                            name="termsAccepted"
                            component="div"
                            className="text-danger small mt-1"
                          />
                        </div>

                        <div className="form-check">
                          <Field
                            type="checkbox"
                            name="informationConfirmed"
                            id="informationConfirmed"
                            className={`form-check-input ${
                              touched.informationConfirmed &&
                              errors.informationConfirmed
                                ? "is-invalid"
                                : ""
                            }`}
                          />

                          <label
                            htmlFor="informationConfirmed"
                            className="form-check-label"
                          >
                            I confirm that the information provided in this
                            application is accurate.
                            <span className="text-danger"> *</span>
                          </label>

                          <ErrorMessage
                            name="informationConfirmed"
                            component="div"
                            className="text-danger small mt-1"
                          />
                        </div>
                      </div>
                    )}

                    {/* Submit */}
                    <div className="text-center">
                      <button
                        type="submit"
                        className={`btnPrimary px-5 py-3 ${isSubmitting ? "disabled" : ""}`}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            {isEditMode
                              ? "Updating Profile..."
                              : "Submitting Application..."}
                            <span className="ms-2">
                              <i className="fa fa-spinner fa-spin fa-fw"></i>
                            </span>
                          </>
                        ) : isEditMode ? (
                          "Update Profile"
                        ) : (
                          "Submit Influencer Application"
                        )}
                      </button>

                      {!isEditMode && (
                        <p className="text-muted small mt-3">
                          Your application will be reviewed by our team before
                          your influencer account is approved.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </section>
  );
};

export default AffiliateSignup;
